import type { SessionView } from "@gsignatures/core";
import { useId, useState } from "react";
import type { SignerApi } from "../api/types";
import { mailto } from "../lib/format";
import { S, type Who } from "../strings";
import { Icon } from "./Icon";
import { Modal } from "./Modal";

export function HelpModal({ w, onClose, toast }: { w: Who; onClose: () => void; toast: (m: string) => void }) {
  const copy = () => {
    if (!navigator.clipboard) {
      toast(S.common.copyFailed);
      return;
    }
    navigator.clipboard.writeText(w.contactEmail).then(
      () => toast(S.common.copied),
      () => toast(S.common.copyFailed),
    );
  };
  return (
    <Modal
      title={S.modals.helpTitle}
      onClose={onClose}
      icon={
        <div className="badge-ico small-badge" aria-hidden="true">
          <Icon n="help" />
        </div>
      }
    >
      <p className="muted">{S.modals.helpIntro(w)}</p>
      <div className="contact-row">
        <Icon n="mail" size={18} />
        <a className="mono contact-email" href={mailto(w.contactEmail)}>
          {w.contactEmail}
        </a>
        <button type="button" className="btn-link" onClick={copy}>
          <Icon n="copy" size={15} /> {S.common.copy}
        </button>
      </div>
      <dl className="faq">
        {S.modals.faq.map((f) => (
          <div key={f.q}>
            <dt>{f.q}</dt>
            <dd className="muted">{f.a(w)}</dd>
          </div>
        ))}
      </dl>
    </Modal>
  );
}

export function PaperModal({ onClose, onConfirm }: { onClose: () => void; onConfirm: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={S.modals.paperTitle} onClose={onClose}>
      <p className="muted">{S.modals.paperText}</p>
      <div className="row end">
        <button type="button" className="btn btn-quiet" onClick={onClose} data-autofocus>
          {S.modals.paperKeep}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy}
          aria-busy={busy}
          onClick={async () => {
            setBusy(true);
            await onConfirm();
            setBusy(false);
          }}
        >
          {S.modals.paperGo}
        </button>
      </div>
    </Modal>
  );
}

export function DeclineModal({ w, onClose, onConfirm }: { w: Who; onClose: () => void; onConfirm: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const id = useId();
  return (
    <Modal title={S.modals.declineTitle} onClose={onClose}>
      <p className="muted">{S.modals.declineText(w)}</p>
      <div className="field">
        <label htmlFor={id}>
          {S.modals.declineReason} <span className="opt">{S.modals.declineReasonHint(w)}</span>
        </label>
        <textarea id={id} className="input textarea tall" maxLength={500} placeholder={S.modals.declinePlaceholder} value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
      <div className="row end">
        <button type="button" className="btn btn-quiet" onClick={onClose} data-autofocus>
          {S.modals.declineBack}
        </button>
        <button
          type="button"
          className="btn btn-destructive"
          disabled={busy}
          aria-busy={busy}
          onClick={async () => {
            setBusy(true);
            await onConfirm(reason.trim());
            setBusy(false);
          }}
        >
          {S.modals.declineGo}
        </button>
      </div>
    </Modal>
  );
}

export function DownloadModal({ api, session, onClose }: { api: SignerApi; session: SessionView; onClose: () => void }) {
  return (
    <Modal title={S.modals.downloadTitle} onClose={onClose}>
      <p className="muted">{S.modals.downloadText}</p>
      <ul className="doc-list compact">
        {session.documents.map((d) => (
          <li className="doc-row" key={d.id}>
            <div className={`thumb ${d.kind === "form" ? "form" : ""}`} aria-hidden="true" />
            <div>
              <b>{d.title}</b> {d.kind === "form" && <span className="faint">{S.modals.blankSuffix}</span>}
            </div>
            <a className="btn btn-quiet" href={api.fileUrl(d.id)} download target="_blank" rel="noreferrer" aria-label={`${S.viewer.download} ${d.title}`}>
              <Icon n="download" /> {S.viewer.download}
            </a>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
